import React from "react";
import {
  MapPin,
  CalendarBlank,
  Globe,
  Crown,
  LockKey,
  Users,
  Storefront,
  CurrencyDollar,
  Phone,
  EnvelopeSimple,
  Sparkle,
  Ticket,
  ChatCircleText,
} from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ShareButtons } from "@/components/ShareButtons";

export const ShowDetailModal = ({ show, open, onOpenChange, onUpgrade, onInquire, onTickets }) => {
  if (!show) return null;
  const locked = show.locked;
  const pd = show.premium_data;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="show-detail-modal"
        className="max-h-[90vh] overflow-y-auto border-white/10 bg-[#12141F] p-0 text-white sm:max-w-2xl"
      >
        <div className="relative h-48 w-full overflow-hidden rounded-t-lg">
          <img src={show.image_url} alt={show.name} className="h-full w-full object-cover opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#12141F] via-[#12141F]/50 to-transparent" />
          <div className="absolute bottom-4 left-6 right-6">
            <div className="flex flex-wrap items-center gap-2">
              <Badge className="border-0 bg-blue-600 text-white">{show.category}</Badge>
              {show.is_premium && (
                <Badge className="border border-amber-400/40 bg-amber-400/15 text-amber-300">
                  <Crown size={12} weight="fill" className="mr-1" /> Premium
                </Badge>
              )}
            </div>
          </div>
        </div>

        <div className="px-6 pb-6">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-bold tracking-tight text-white">
              {show.name}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Full details for {show.name} in {show.city}, {show.country}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <InfoRow icon={<MapPin size={16} weight="fill" className="text-blue-400" />} label="Location" value={`${show.venue ? show.venue + ", " : ""}${show.city}, ${show.country}`} />
            <InfoRow icon={<CalendarBlank size={16} className="text-blue-400" />} label="Dates" value={`${show.start_date}${show.end_date ? " → " + show.end_date : ""}`} />
            {show.website && (
              <InfoRow
                icon={<Globe size={16} className="text-blue-400" />}
                label="Website"
                value={
                  <a href={show.website} target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">
                    {show.website.replace(/^https?:\/\//, "")}
                  </a>
                }
              />
            )}
          </div>

          <p className="mt-5 text-sm leading-relaxed text-slate-300">{show.description}</p>

          {/* Premium section */}
          {show.is_premium && (
            <div className="mt-6 rounded-2xl border border-amber-400/25 bg-amber-400/[0.04] p-5">
              <div className="mb-4 flex items-center gap-2">
                <Sparkle size={18} weight="fill" className="text-amber-300" />
                <h4 className="font-display text-base font-bold text-amber-200">Exclusive Deal Intelligence</h4>
              </div>

              {locked ? (
                <div className="flex flex-col items-center gap-4 py-4 text-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-400/15 ring-1 ring-amber-400/40">
                    <LockKey size={28} weight="fill" className="text-amber-300" />
                  </div>
                  <p className="max-w-sm text-sm text-slate-300">
                    Unlock verified exhibitor counts, attendee data, top brands, investment ranges and direct organizer contacts.
                  </p>
                  <button
                    data-testid="detail-upgrade-btn"
                    onClick={onUpgrade}
                    className="rounded-full bg-amber-400 px-6 py-3 text-sm font-bold text-black transition-colors duration-200 hover:bg-amber-300"
                  >
                    Unlock all premium data — $9.99 one-time
                  </button>
                </div>
              ) : pd ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <Stat icon={<Storefront size={16} className="text-amber-300" />} label="Exhibitors" value={pd.exhibitor_count ? `${pd.exhibitor_count}+` : "—"} />
                    <Stat icon={<Users size={16} className="text-amber-300" />} label="Attendees" value={pd.attendee_count ? pd.attendee_count.toLocaleString() + "+" : "—"} />
                  </div>
                  <Stat icon={<CurrencyDollar size={16} className="text-amber-300" />} label="Investment Range" value={pd.investment_range} full />
                  {pd.top_brands?.length > 0 && (
                    <div>
                      <p className="mb-2 text-xs uppercase tracking-wider text-slate-500">Top Brands</p>
                      <div className="flex flex-wrap gap-2">
                        {pd.top_brands.map((b) => (
                          <span key={b} className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white">{b}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {pd.deal_highlights?.length > 0 && (
                    <div>
                      <p className="mb-2 text-xs uppercase tracking-wider text-slate-500">Deal Highlights</p>
                      <ul className="space-y-1.5">
                        {pd.deal_highlights.map((h) => (
                          <li key={h} className="flex items-start gap-2 text-sm text-slate-300">
                            <Sparkle size={14} weight="fill" className="mt-0.5 shrink-0 text-amber-300" /> {h}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {pd.key_contacts?.length > 0 && (
                    <div>
                      <p className="mb-2 text-xs uppercase tracking-wider text-slate-500">Organizer Contacts</p>
                      {pd.key_contacts.map((c) => (
                        <div key={c.email} className="rounded-xl border border-white/10 bg-white/5 p-3 text-sm">
                          <p className="font-semibold text-white">{c.name}</p>
                          <p className="mt-1 flex items-center gap-1.5 text-slate-300"><EnvelopeSimple size={14} /> {c.email}</p>
                          <p className="mt-0.5 flex items-center gap-1.5 text-slate-300"><Phone size={14} /> {c.phone}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className="py-4 text-center text-sm text-slate-400">
                  Detailed deal intelligence for this show is being compiled and will appear here shortly.
                </p>
              )}
            </div>
          )}

          {/* Affiliate CTAs */}
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <button
              data-testid="detail-inquire-btn"
              onClick={() => onInquire(show)}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-white/10 bg-white/5 px-5 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:border-white/25 hover:bg-white/10"
            >
              <ChatCircleText size={17} weight="fill" /> Inquire
            </button>
            <button
              data-testid="detail-tickets-btn"
              onClick={() => onTickets(show)}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-blue-500"
            >
              <Ticket size={17} weight="fill" /> Get Tickets
            </button>
          </div>
          <div className="mt-4">
            <ShareButtons show={show} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const InfoRow = ({ icon, label, value }) => (
  <div className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3">
    <div className="mt-0.5">{icon}</div>
    <div className="min-w-0">
      <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
      <p className="mt-0.5 break-words text-sm text-white">{value}</p>
    </div>
  </div>
);

const Stat = ({ icon, label, value, full }) => (
  <div className={`rounded-xl border border-white/10 bg-white/[0.03] p-3 ${full ? "col-span-2" : ""}`}>
    <p className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-slate-500">{icon} {label}</p>
    <p className="mt-1 text-base font-semibold text-white">{value || "—"}</p>
  </div>
);
